import React, { useEffect, useState } from "react";
import { ViewerSettings } from "../../types/settings";

type LanguageCode = ViewerSettings["language"];

const TUTORIAL_MODAL_COPY: Record<
  LanguageCode,
  {
    title: string;
    subtitle: string;
    skip: string;
    confirm: string;
  }
> = {
  en: {
    title: "Getting Started",
    subtitle: "Learn basic interactions",
    skip: "Skip Tutorial",
    confirm: "Got It",
  },
  es: {
    title: "Primeros pasos",
    subtitle: "Aprende las interacciones b\u00e1sicas",
    skip: "Omitir tutorial",
    confirm: "Entendido",
  },
  fr: {
    title: "Premiers pas",
    subtitle: "D\u00e9couvrez les interactions de base",
    skip: "Ignorer le tutoriel",
    confirm: "Compris",
  },
  de: {
    title: "Erste Schritte",
    subtitle: "Lernen Sie die grundlegenden Interaktionen",
    skip: "Tutorial \u00fcberspringen",
    confirm: "Verstanden",
  },
  zh: {
    title: "\u5f00\u59cb\u4f7f\u7528",
    subtitle: "\u4e86\u89e3\u57fa\u672c\u4ea4\u4e92",
    skip: "\u8df3\u8fc7\u6559\u7a0b",
    confirm: "\u77e5\u9053\u4e86",
  },
};

interface InstructionItem {
  id: string;
  icon: string;
  title: string;
  description: string;
}

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
  instructions?: InstructionItem[];
  onInstructionSelect?: (instructionId: string) => void;
  language?: LanguageCode;
  title?: string;
  subtitle?: string;
  titleIcon?: string;
  skipButtonText?: string;
  confirmButtonText?: string;
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  iconColor?: string;
  accentColor?: string;
  fontFamily?: string;
}

export function TutorialModal({
  isOpen,
  onClose,
  instructions = [
    {
      id: "pinch-to-zoom",
      icon: "pinch",
      title: "Pinch to Zoom",
      description: "Pinch to zoom in or out of the model.",
    },
    {
      id: "two-finger-drag",
      icon: "pan_tool",
      title: "Two-Finger Drag",
      description: "Drag with two fingers to move the object.",
    },
    {
      id: "tap-for-info",
      icon: "ads_click",
      title: "Tap for Info",
      description: "Tap a part of the model to view more information.",
    },
    {
      id: "one-finger-rotate",
      icon: "3d_rotation",
      title: "One-Finger Rotate",
      description: "Drag with one finger to rotate the model.",
    },
  ],

  onInstructionSelect,
  language = "en",
  title,
  subtitle,
  titleIcon = "school",
  skipButtonText,
  confirmButtonText,
  backgroundColor = "rgba(255, 255, 255, 0.04)",
  backdropBlur = "40px",
  borderColor = "rgba(255, 255, 255, 0.1)",
  boxShadow = "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
  primaryTextColor = "rgba(255, 255, 255, 0.95)",
  secondaryTextColor = "rgba(255, 255, 255, 0.7)",
  iconColor = "rgba(255, 255, 255, 0.8)",
  accentColor = "rgb(56, 189, 248)",
  fontFamily = "Arial, sans-serif",
}: TutorialModalProps) {
  const copy = TUTORIAL_MODAL_COPY[language] ?? TUTORIAL_MODAL_COPY.en;
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Delay to trigger animation
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
      // Wait for animation to complete before unmounting
      setTimeout(() => setShouldRender(false), 300);
    }
  }, [isOpen]);

  if (!shouldRender) return null;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(15, 17, 21, 0.6)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
        fontFamily,
      }}
      onClick={onClose}
      data-oid="zrffwg4"
    >
      <div
        style={{
          width: "540px",
          maxWidth: "90vw",
          borderRadius: "2rem",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          backgroundColor: backgroundColor,
          backdropFilter: `blur(${backdropBlur})`,
          WebkitBackdropFilter: `blur(${backdropBlur})`,
          border: `1px solid ${borderColor}`,
          boxShadow: boxShadow,
          transform: isVisible ? "scale(1)" : "scale(0.95)",
          transition: "transform 0.3s ease-in-out",
        }}
        onClick={(e) => e.stopPropagation()}
        data-oid="kc2i20p"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "1.5rem",
            right: "1.5rem",
            width: "2.5rem",
            height: "2.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "9999px",
            background: "transparent",
            border: "none",
            color: secondaryTextColor,
            cursor: "pointer",
            transition: "all 0.3s",
            zIndex: 10,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
            e.currentTarget.style.color = primaryTextColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = secondaryTextColor;
          }}
          data-oid="i0ai.5h"
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "1.25rem", fontWeight: 300 }}
            data-oid="ogozl0-"
          >
            close
          </span>
        </button>

        {/* Header */}
        <div style={{ padding: "2rem 2rem 1rem 2rem" }} data-oid="uanp2e-">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              marginBottom: "0.25rem",
            }}
            data-oid="5u.q4k7"
          >
            <span
              className="material-symbols-outlined"
              style={{
                color: accentColor,
                fontSize: "1.5rem",
                fontWeight: 300,
              }}
              data-oid="li60hai"
            >
              {titleIcon}
            </span>
            <h2
              style={{
                margin: 0,
                fontSize: "1.25rem",
                fontWeight: 700,
                letterSpacing: "0.025em",
                color: primaryTextColor,
                fontFamily,
              }}
              data-oid="-rfr17o"
            >
              {title ?? copy.title}
            </h2>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "0.875rem",
              fontWeight: 400,
              color: secondaryTextColor,
              fontFamily,
            }}
            data-oid="07.8t-8"
          >
            {subtitle ?? copy.subtitle}
          </p>
        </div>

        {/* Instruction Items */}
        <div
          style={{
            padding: "1rem 2rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
            overflowY: "auto",
            maxHeight: "65vh",
          }}
          data-oid="by_l52m"
        >
          {instructions.map((item, index) => (
            <button
              key={index}
              type="button"
              onClick={() => onInstructionSelect?.(item.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1.5rem",
                width: "100%",
                padding: "1rem",
                borderRadius: "1rem",
                background: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.05)",
                transition: "all 0.3s",
                textAlign: "left",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.06)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.03)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.05)";
              }}
              data-oid=".y_z:c3"
            >
              <div
                style={{
                  width: "3rem",
                  height: "3rem",
                  borderRadius: "0.75rem",
                  background: `${accentColor}1A`, // 10% opacity
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
                data-oid=":34n5bf"
              >
                <span
                  className="material-symbols-outlined"
                  style={{
                    color: iconColor,
                    fontSize: "1.5rem",
                    fontWeight: 300,
                  }}
                  data-oid="spse2_g"
                >
                  {item.icon}
                </span>
              </div>
              <div data-oid="ui227vh">
                <h4
                  style={{
                    margin: 0,
                    marginBottom: "0.125rem",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: primaryTextColor,
                    fontFamily,
                  }}
                  data-oid="y_6251c"
                >
                  {item.title}
                </h4>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    color: secondaryTextColor,
                    lineHeight: 1.6,
                    fontFamily,
                  }}
                  data-oid="j:583fw"
                >
                  {item.description}
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "2rem",
            marginTop: "0.5rem",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "1.5rem",
            borderTop: "1px solid rgba(255, 255, 255, 0.05)",
          }}
          data-oid="0owdv60"
        >
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "0.75rem",
              fontWeight: 500,
              color: secondaryTextColor,
              cursor: "pointer",
              transition: "color 0.3s",
              fontFamily,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = primaryTextColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = secondaryTextColor;
            }}
            data-oid="9mn_zj7"
          >
            {skipButtonText ?? copy.skip}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: "0.75rem 2.5rem",
              fontSize: "0.75rem",
              fontWeight: 700,
              borderRadius: "9999px",
              background: accentColor,
              color: "white",
              border: "none",
              cursor: "pointer",
              boxShadow: `0 0 20px ${accentColor}4D`, // 30% opacity
              transition: "all 0.3s",
              fontFamily,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = accentColor.replace(
                "248",
                "228",
              ); // Lighter shade
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = accentColor;
              e.currentTarget.style.transform = "translateY(0)";
            }}
            data-oid="g_pl70g"
          >
            {confirmButtonText ?? copy.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}
